import { Anchor, Text } from "@mantine/core";
import { useState, type ReactNode } from "react";

import { DownloadError, downloadSignedFile } from "../../api/download";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { outputFileName } from "./file-names";

/** Why a download did not start, in words; `expired` when a fresh URL should fix it. */
function problemOf(error: unknown): { text: string; expired: boolean } {
  const status = error instanceof DownloadError ? error.status : undefined;
  if (status === 403 || status === 404 || status === 410) {
    return { text: "The file's link had expired; it is fresh now, so try again.", expired: true };
  }
  if (status === undefined) return { text: "The file could not be fetched here.", expired: false };
  return { text: `The file could not be fetched (status ${status}).`, expired: false };
}

/**
 * Saves a delivered output as a file named for the job (G1 decision 19), or `fileName` where a
 * page names its own (a sound, live's name). `busy` while it runs; `result`, after a failure,
 * says why and offers the file in a new tab. An expired link reads the job again for a fresh one.
 */
export function useDownloadOutput(
  job: Job,
  output: GenerationOutput,
  readAgain: () => void,
  fileName?: string,
) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const url = output.url;
  const start = async () => {
    if (url === null || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await downloadSignedFile(url, fileName ?? outputFileName(job, output));
    } catch (error) {
      const { text, expired } = problemOf(error);
      setProblem(text);
      if (expired) readAgain();
    } finally {
      setBusy(false);
    }
  };
  let result: ReactNode = null;
  if (problem !== null && url !== null) {
    result = (
      <Text size="sm" role="alert">
        {problem}{" "}
        <Anchor
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          c="inherit"
          underline="always"
        >
          Open it in a new tab
        </Anchor>
        .
      </Text>
    );
  }
  return { start: () => void start(), busy, result };
}
